import type { BackendAPITypes } from '../generated/blueprint.ts';
import type { CurrencyEnum } from '../generated/enums.ts';

export type Money = BackendAPITypes.Money;

export enum DisplayLocale {
    NL_NL = "nl-NL",
    EN_US = "en-US",
    DE_DE = "de-DE",
    FR_FR = "fr-FR",
}
export const MAX_SAFE_DECIMAL = Number.MAX_SAFE_INTEGER / 100;

export const isDecimalInSafeRange = (amountInDecimal: number): boolean =>
    Number.isFinite(amountInDecimal) && Math.abs(amountInDecimal) <= MAX_SAFE_DECIMAL;


const validateFiniteNumber = (value: number, context: string): void => {
    if (!Number.isFinite(value)) {
        throw new Error(`Invalid ${context}: ${value}. Expected a finite number.`);
    }
};

const validateDecimalInSafeRange = (value: number, context: string): void => {
    if (!isDecimalInSafeRange(value)) {
        throw new Error(
            `Invalid ${context}: ${value}. Amount is outside the safe range (max ±${MAX_SAFE_DECIMAL}).`
        );
    }
};

export const isMoney = (value: unknown): value is Money => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'signed_amount' in value &&
    'currency' in value &&
    typeof (value as any).signed_amount === 'number' &&
    typeof (value as any).currency === 'string'
  );
};

export const toMoneyFromCents = (amountInCents: number, currency: CurrencyEnum): Money => {
    validateFiniteNumber(amountInCents, 'amountInCents');
    if (!Number.isInteger(amountInCents)) {
        throw new Error(`Invalid amountInCents: ${amountInCents}. Expected an integer.`);
    }
    return {
        signed_amount: amountInCents,
        currency: currency,
    };
}

export const toDecimalFromMoney = (money: Money): number => {
    validateFiniteNumber(money.signed_amount, 'money.signed_amount');
    return money.signed_amount / 100;
}

export const toCentsFromMoney = (money: Money): number => {
    validateFiniteNumber(money.signed_amount, 'money.signed_amount');
    return money.signed_amount;
}

export const fromDecimalToMoney = (amountInDecimal: number, currency: CurrencyEnum): Money => {
    validateFiniteNumber(amountInDecimal, 'amountInDecimal');
    validateDecimalInSafeRange(amountInDecimal, 'amountInDecimal');
    return {
        signed_amount: Math.round(amountInDecimal * 100),
        currency: currency,
    };
}

export const decimalToLocalizedString = (
    decimalValue: number,
    locale: DisplayLocale = DisplayLocale.NL_NL,
    fractionDigits: number = 2
): string => {
    if (!Number.isInteger(fractionDigits) || fractionDigits < 0 || fractionDigits > 100) {
        throw new Error(
            `Invalid fractionDigits: ${fractionDigits}. Expected an integer between 0 and 100.`
        );
    }
    return decimalValue.toLocaleString(locale, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    });
};

export const moneyToLocalizedString = (
    money: Money | undefined,
    locale: DisplayLocale = DisplayLocale.NL_NL,
    fractionDigits: number = 2,
    absoluteValue: boolean = false
): string => {
    if (!money) return decimalToLocalizedString(0, locale, fractionDigits);
    const amount = absoluteValue ? Math.abs(money.signed_amount) : money.signed_amount;
    return decimalToLocalizedString(amount / 100, locale, fractionDigits);
};

export const centsToLocalizedString = (
    amountInCents: number,
    locale: DisplayLocale = DisplayLocale.NL_NL,
    fractionDigits: number = 2
): string => {
    return decimalToLocalizedString(amountInCents / 100, locale, fractionDigits);
};

